/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../providers/WalletProvider';
import { marketApi } from '../api/market';
import { GlobalMarketData, MarketToken } from '../types/market';
import { MARKET_REFRESH_INTERVAL } from '../lib/constants';

export const useMarketData = () => {
    const { network, chain } = useWallet();
    const [tokens, setTokens] = useState<MarketToken[]>([]);
    const [stats, setStats] = useState<GlobalMarketData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        // Don't set loading to true on refresh to avoid flickering UI
        if (tokens.length === 0) setIsLoading(true);
        
        try {
            const [marketTokens, marketStats] = await Promise.all([
                marketApi.getTokens(chain || 'SOL', network),
                marketApi.getGlobalStats(),
            ]);

            setTokens(marketTokens);
            setStats(marketStats);
            setError(null);
        } catch (e: any) {
            console.error("Market Data Error:", e);
            setError("Failed to load market data.");
        } finally {
            setIsLoading(false);
        }
    }, [network, chain]);

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, MARKET_REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [refresh]);

    return { tokens, stats, isLoading, error, refresh };
};